/**
 * Authenticated admin API helpers — session cookie + optional x-admin-token.
 * Used by the owner portal after login (Phase A/B auth).
 */

const API_URL = import.meta.env.VITE_API_URL || '';
const ADMIN_TOKEN = import.meta.env.VITE_ARTICLES_ADMIN_TOKEN || '';

/** Paths that must stay unauthenticated (login/logout). */
const PUBLIC_ADMIN_API_PREFIXES = ['/api/admin/login', '/api/admin/logout'];

export function resolveAdminApiUrl(input: string): string {
  if (input.startsWith('http://') || input.startsWith('https://')) return input;
  const path = input.startsWith('/') ? input : `/${input}`;
  return API_URL ? `${API_URL}${path}` : path;
}

export function shouldAttachAdminAuth(url: string): boolean {
  if (!url.includes('/api/')) return false;
  return !PUBLIC_ADMIN_API_PREFIXES.some((p) => url.includes(p));
}

/** Headers for manual fetch calls (token + JSON defaults). */
export function adminHeaders(extra?: HeadersInit): HeadersInit {
  const h = new Headers(extra);
  if (ADMIN_TOKEN && !h.has('x-admin-token')) {
    h.set('x-admin-token', ADMIN_TOKEN);
  }
  if (!h.has('Content-Type') && !(extra && 'Content-Type' in (extra as Record<string, string>))) {
    // leave Content-Type unset unless caller sets it
  }
  return h;
}

/**
 * fetch() wrapper: credentials include session cookie; adds x-admin-token when configured.
 */
export async function adminFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const url = resolveAdminApiUrl(typeof input === 'string' ? input : String(input));
  const attach = shouldAttachAdminAuth(url);
  const headers = new Headers(init?.headers);
  if (attach && ADMIN_TOKEN && !headers.has('x-admin-token')) {
    headers.set('x-admin-token', ADMIN_TOKEN);
  }
  return fetch(url, {
    ...init,
    headers,
    credentials: attach ? 'include' : init?.credentials,
  });
}

export async function adminLogin(
  email: string,
  password: string,
  rememberMe = true
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await adminFetch('/api/admin/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, rememberMe }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: (data as { error?: string }).error || 'Login failed' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not reach the server. Check your connection.' };
  }
}

export async function adminLogout(): Promise<void> {
  try {
    await adminFetch('/api/admin/logout', { method: 'POST', credentials: 'include' });
  } catch {
    /* ignore */
  }
}

export async function checkAdminSession(): Promise<{ ok: boolean; email?: string; legacy?: boolean }> {
  try {
    const res = await adminFetch('/api/admin/session', { credentials: 'include' });
    if (!res.ok) return { ok: false };
    const data = await res.json();
    return { ok: true, email: data.email, legacy: data.legacy === true };
  } catch {
    return { ok: false };
  }
}
